import { findMostSpecificRoute } from 'quartz-routing/internal/routeMatchingUtils';
import { preloadGraniteRemote } from '../internal/module-federation/runtime';
import { currentPackageName, findOwningGraniteRemote, getBlocklistedRoutes, getRouteIndex } from './granite-config';
function isSameOrigin(url) {
  return url.origin === window.location.origin;
}
function parseLinkUrl(url) {
  try {
    let parsed;
    if (url.startsWith('/')) {
      parsed = new URL(url, window.origin);
    } else {
      parsed = new URL(url);
    }
    return parsed;
  } catch (_unused) {
    return null;
  }
}
function matchRedirect(pathname, redirects) {
  for (const redirect of redirects) {
    try {
      const match = new RegExp(redirect.from).exec(pathname);
      // Require a full-string match. `exec`/`match` succeed on a partial match
      // anywhere in the string unless the pattern is anchored, so an unanchored
      // worker-supplied rule (e.g. `/old/(.*)`) would otherwise misdirect a path
      // like `/prefix/old/foo`.
      if (match && match.index === 0 && match[0].length === pathname.length) {
        return redirect.to.replace(/\$(\d+)/g, (_, n) => {
          var _match$parseInt;
          return (_match$parseInt = match[parseInt(n, 10)]) !== null && _match$parseInt !== void 0 ? _match$parseInt : '';
        });
      }
    } catch (_unused2) {
      // invalid regex — skip
    }
  }
  return null;
}

/**
 * Mirror the granite-config-worker's server-side redirects client-side so a
 * soft navigation lands on the same final path the server would have produced.
 * No-op (returns the input) until the route index has loaded.
 */
function followRedirects(pathname) {
  const routeIndex = getRouteIndex();
  if (!routeIndex) {
    return pathname;
  }
  let finalPath = pathname;
  for (let hop = 0; hop < 10; hop++) {
    const dest = matchRedirect(finalPath, routeIndex.redirects);
    if (!dest) {
      break;
    }
    finalPath = dest;
  }
  return finalPath;
}
export function bindLinkRewriter(history, gates) {
  function linkRewriterFn(e) {
    if (!(e.target instanceof HTMLElement)) {
      return;
    }
    const link = e.target.closest('a');
    if (!link) {
      return;
    }
    if (link.target === '_blank' || link.hasAttribute('download') || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
      return;
    }
    const href = link.getAttribute('href');
    if (!href) {
      return;
    }
    const linkUrl = parseLinkUrl(href);
    if (!linkUrl || !isSameOrigin(linkUrl)) {
      return;
    }

    // Follow server-side redirects first, then route on the resolved path.
    const finalPath = followRedirects(linkUrl.pathname);
    const blocklisted = findMostSpecificRoute(getBlocklistedRoutes(), finalPath);
    const ownerServiceId = blocklisted ? null : findOwningGraniteRemote(finalPath, gates);
    if (ownerServiceId !== null) {
      if (ownerServiceId !== currentPackageName) {
        preloadGraniteRemote(ownerServiceId);
      }
      e.preventDefault();
      history.push(finalPath + linkUrl.search + linkUrl.hash);
      return;
    }

    // A redirect resolved to a non-granite path — hard-nav to it so the
    // server-side redirect is honored without a wasted hop through the old URL.
    if (finalPath !== linkUrl.pathname) {
      e.preventDefault();
      window.location.href = finalPath + linkUrl.search + linkUrl.hash;
    }
  }
  document.addEventListener('click', linkRewriterFn, true);
  return () => {
    document.removeEventListener('click', linkRewriterFn, true);
  };
}